import { Button, Icon, pxToRem, Text, useFormState } from '@lbcamden/lib-react';
import { getQueryKey } from '@trpc/react-query';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { routes } from '~/client/config/routes';
import { useIsUserRegistered, useHandlePinAuth } from '~/client/hooks/authHooks';
import { useCurrentUser } from '~/client/hooks/storeHooks';
import { queryClient } from '~/client/lib/queryClient';
import { api } from '~/client/lib/trpc';
import { useAppStore } from '~/client/store/app/appStore';
import { newUserPinSchema, userPinLength } from '~/common/schemas/auth';

import { PinInput } from '../../common/PinInput';
import { SplashPage } from '../SplashPage';
import { RegistrationStep, stepCopy } from './config';
import { ButtonsContainer, PinContainer } from './UserPinRegistrationPage.styled';

export const UserPinRegistrationPage: FunctionComponent = () => {
  const navigate = useNavigate();

  const user = useCurrentUser();

  const loginRedirectUri = useAppStore((state) => state.loginRedirectUri);

  const { isRegistered } = useIsUserRegistered();
  const { onPinVerified } = useHandlePinAuth();

  const [step, setStep] = useState<RegistrationStep>('firstEntry');
  const [loading, setLoading] = useState(false);

  const { data, updateField, refreshForm, validationErrors } = useFormState(
    { value: '', repeatValue: '' },
    newUserPinSchema,
  );

  const { mutateAsync: registerUserPin, error: registerError } = api.registerUserPin.useMutation();

  const { title, message } = stepCopy[step];
  const isFirstEntry = step === 'firstEntry';
  const isSecondEntry = step === 'secondEntry';
  const isSuccess = step === 'success';
  const fieldKey = isFirstEntry ? 'value' : 'repeatValue';

  const hasValidValue = !validationErrors?.fieldErrors.value;
  const hasValidRepeatValue = !validationErrors;
  const showValidationError = data[fieldKey].length === userPinLength;
  const validationErrorMessage = validationErrors?.fieldErrors[fieldKey]?.join(', ');
  const errorMessage = registerError?.message || (showValidationError && validationErrorMessage);

  const canContinue = (isFirstEntry && hasValidValue) || hasValidRepeatValue;

  const handleRegistration = async () => {
    if (!user || validationErrors) {
      return;
    }
    setLoading(true);
    try {
      await registerUserPin(data);
      await onPinVerified(user, data.value);
      void queryClient.invalidateQueries({ queryKey: getQueryKey(api.hasUserPin) });
      setStep('success');
    } finally {
      setLoading(false);
    }
  };

  const onClickRestart = () => {
    setStep('firstEntry');
    refreshForm({ value: '', repeatValue: '' });
  };

  const onClickContinue = () => {
    switch (step) {
      case 'firstEntry':
        return hasValidValue && setStep('secondEntry');
      case 'secondEntry':
        return hasValidRepeatValue && handleRegistration();
      case 'success':
        return navigate(loginRedirectUri || routes.home);
      default:
    }
  };

  useEffect(() => {
    if (!user) {
      void navigate(routes.login, { replace: true });
      return;
    }
    if (isRegistered && !isSuccess) {
      // User is already registered
      void navigate(routes.home, { replace: true });
    }
  }, [user, isRegistered]);

  return (
    <SplashPage>
      <Text variant="h1">{title}</Text>
      <Text variant="lead">{message}</Text>

      {!isSuccess && (
        <PinContainer>
          <PinInput
            key={step} // Ensures auto-focus is retriggered on step change
            value={data[fieldKey]}
            isDisabled={loading}
            onChange={(newVal) => updateField(fieldKey, newVal)}
          />
          {errorMessage && (
            <Text variant="body2" $color="error">
              {errorMessage}
            </Text>
          )}
        </PinContainer>
      )}

      <ButtonsContainer>
        {isSecondEntry && (
          <Button $variant="secondary" disabled={loading} onClick={onClickRestart}>
            <Icon icon="ui/refresh" width={pxToRem(14)} />
            Restart
          </Button>
        )}
        <Button loading={loading} disabled={!canContinue} onClick={onClickContinue}>
          Continue
        </Button>
      </ButtonsContainer>
    </SplashPage>
  );
};
